import { EDITOR_DESKTOP_MEDIA_QUERY } from '@/components/app-editor/use-editor-chrome';
import { TemplateString } from '@/components/template-string';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import type { CreateWebsiteBannerDto } from '@/generated/korza-api-client/types.gen';
import { useMediaQuery } from '@/hooks/use-mobile';
import { useI18nContext } from '@/i18n/i18n-react';
import { LazyMonaco } from './lazy-monaco';

const INLINE_CODE_CLASSNAME =
  'relative rounded bg-muted px-[0.3rem] py-[0.2rem] font-mono text-xs';

export interface ContentPanelProps {
  form: CreateWebsiteBannerDto;
  updateContent: (patch: Partial<CreateWebsiteBannerDto['content']>) => void;
}

/** Banner text (HTML) and button text. Monaco only from 1024px, a textarea below (D8). */
export function ContentPanel({ form, updateContent }: ContentPanelProps) {
  const { LL } = useI18nContext();
  const isDesktop = useMediaQuery(EDITOR_DESKTOP_MEDIA_QUERY);

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="banner-text-html">
          {LL.websiteBannerPage.content.bannerTextLabel()}
        </Label>
        {isDesktop ? (
          <LazyMonaco
            language="html"
            value={form.content.textHtml}
            onChange={(textHtml) => updateContent({ textHtml })}
            minHeight="100px"
          />
        ) : (
          <Textarea
            id="banner-text-html"
            className="min-h-[100px] font-mono text-base"
            value={form.content.textHtml}
            onChange={(e) => updateContent({ textHtml: e.target.value })}
          />
        )}
        <p className="text-sm text-muted-foreground">
          <TemplateString
            templateString={LL.websiteBannerPage.content.bannerTextDescription()}
            componentMap={{
              bTag: () => (
                <code className={INLINE_CODE_CLASSNAME}>&lt;b&gt;</code>
              ),
              boldExample: () => (
                <code className={INLINE_CODE_CLASSNAME}>
                  {LL.websiteBannerPage.content.bannerTextDescriptionBoldExample()}
                </code>
              ),
            }}
          />
        </p>
      </div>
      <div className="space-y-2">
        <Label htmlFor="banner-button-text">
          {LL.websiteBannerPage.content.buttonTextLabel()}
        </Label>
        <Input
          id="banner-button-text"
          value={form.content.buttonText}
          onChange={(e) => updateContent({ buttonText: e.target.value })}
          placeholder={LL.websiteBannerPage.content.buttonTextPlaceholder()}
        />
        <p className="text-sm text-muted-foreground">
          {LL.websiteBannerPage.content.buttonTextDescription()}
        </p>
      </div>
    </div>
  );
}
